import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Eye, Star, Package, Heart } from 'lucide-react';
import { useWishlist } from '@/hooks/useWishlist';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import type { Produto } from '@/types';
import { useCarrinhoStore } from '@/stores/carrinhoStore';
import { Product3DPreview } from './ThreeDShowcase';

interface ProductCardProps {
  produto: Produto;
  index?: number;
}

export function ProductCard({ produto, index = 0 }: ProductCardProps) {
  const adicionarItem = useCarrinhoStore((state) => state.adicionarItem);
  const { isFavorited, toggleWishlist } = useWishlist();
  const favorited = isFavorited(produto.id);

  const handleToggleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(produto.id);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    adicionarItem(produto, 1);
  };

  const corCategoria = produto.categoria?.cor || '#FF6B35';

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ 
        duration: 0.5, 
        delay: index * 0.1,
        ease: [0.16, 1, 0.3, 1]
      }}
    >
      <Link to={`/produto/${produto.slug}`}>
        <motion.div
          className="group relative bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:border-orange-500/30 transition-all duration-500"
        >
          {/* Subtle Glow on Hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-orange-500/5 to-transparent transition-opacity duration-500" />
          
          {/* Image Container */}
          <div className="relative aspect-square overflow-hidden bg-gray-50 flex items-center justify-center">
            {produto.fotos?.[0] ? (
              <>
                <motion.img
                  src={produto.fotos[0]}
                  alt={produto.nome}
                  className="h-full w-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />
                {/* Overlay on Hover */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center gap-3"
                >
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    whileHover={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1 }}
                  >
                    <Button
                      variant="nuvem"
                      size="sm"
                      leftIcon={<Eye className="w-4 h-4" />}
                      className="bg-white text-gray-900 hover:bg-gray-100"
                    >
                      Ver Detalhes
                    </Button>
                  </motion.div>
                </motion.div>
              </>
            ) : (
              <div className="h-full w-full flex items-center justify-center">
                <Product3DPreview color={corCategoria} />
              </div>
            )}
            
            {/* Esgotado Badge */}
            {produto.estoqueDisponivel === 0 && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center">
                <span className="bg-gray-900 text-white px-6 py-3 rounded-full text-sm font-bold shadow-xl">
                  Esgotado
                </span>
              </div>
            )}
            
            {/* Tags */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/90 backdrop-blur text-gray-900 px-3 py-1 rounded-full text-xs font-semibold shadow-lg"
              >
                {produto.categoria?.nome || 'Produto'}
              </motion.span>
              
              {produto.estoqueDisponivel < 5 && produto.estoqueDisponivel > 0 && (
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-red-500/90 backdrop-blur text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg"
                >
                  {produto.estoqueDisponivel} restantes
                </motion.span>
              )}
            </div>

            {/* Wishlist Button (Heart) */}
            <div className="absolute top-4 right-4 z-10">
              <motion.button
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleToggleWishlist}
                className={`p-2 rounded-full shadow-lg transition-colors ${
                  favorited ? 'bg-rose-500 text-white' : 'bg-white/80 backdrop-blur text-gray-500 hover:text-rose-500'
                }`}
              >
                <Heart className={`w-4 h-4 ${favorited ? 'fill-current' : ''}`} />
              </motion.button>
            </div>

            {/* Quick Add Button */}
            {produto.estoqueDisponivel > 0 && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                onClick={handleAddToCart}
                className="absolute bottom-4 right-4 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-xl hover:bg-orange-500 hover:text-white transition-colors group/btn"
              >
                <ShoppingCart className="w-5 h-5 text-gray-900 group-hover/btn:text-white" />
              </motion.button>
            )}
          </div>

          {/* Content */}
          <div className="p-5 relative">
            {/* Rating */}
            <div className="flex items-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
              <span className="text-xs text-gray-500 ml-1">(127)</span>
            </div>

            {/* Title */}
            <h3 className="font-bold text-gray-900 group-hover:text-orange-500 transition-colors line-clamp-2 mb-2 min-h-[48px] text-lg">
              {produto.nome}
            </h3>

            {/* Price */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-black text-gray-900">
                  {formatCurrency(produto.precoVenda)}
                </p>
                <p className="text-xs text-gray-500 font-medium">
                  ou 3x de {formatCurrency(produto.precoVenda / 3)}
                </p>
              </div>
              
              {/* Stock Indicator */}
              {produto.estoqueDisponivel > 0 && (
                <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
                  <Package className="w-3.5 h-3.5" />
                  <span>EM ESTOQUE</span>
                </div>
              )}
            </div>

            {/* Progress Bar for Low Stock */}
            {produto.estoqueDisponivel < 10 && produto.estoqueDisponivel > 0 && (
              <div className="mt-3">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(produto.estoqueDisponivel / 10) * 100}%` }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                    className="h-full bg-rose-500"
                  />
                </div>
                <p className="text-[10px] text-rose-500 font-bold uppercase tracking-wider mt-1.5">
                  Últimas unidades!
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
}
